export const LimitsPerTimeframes = {
    HOUR: 11,
    DAY: 21,
    WEEK: 141,
    MONTH: 621
};
export const numCapsToStringCaps = (caps) => caps.map((cap) => ({
    ...cap,
    max: cap.max.toString()
}));
export const stringCapsToNumCaps = (caps) => caps.map((cap) => ({
    ...cap,
    max: Number(cap.max) || 0
}));
export const findFrequencyValue = (caps, timeUnit) => caps.find(({ duration: { time_unit } }) => time_unit === timeUnit)?.max;
export const parseFrequencyByTimeframe = (caps) => {
    const capsByTimeframe = {};
    caps.forEach(({ duration: { time_unit }, max }) => {
        /* @ts-expect-error - (TS Upgrade: 5.7.3) - https://typescript.tv/errors/#ts7053 */
        capsByTimeframe[time_unit] = Number(max || 0);
    });
    return capsByTimeframe;
};
/**
 * Compares and validates each frequency unit against its counterparts.
 * @param caps: The frequency caps array associated with the frequencies key.
 * @returns An error object, if any errors were found. Else, undefined.
 */
export const checkFrequencyProportions = (caps) => {
    const { HOUR: hour, DAY: day, WEEK: week, MONTH: month } = parseFrequencyByTimeframe(caps);
    const errors = {};
    if (month) {
        if (month === 0) {
            errors["MONTH"] = "Frequency caps must be higher than 0";
        }
        if (hour && hour > month) {
            errors["MONTH"] = "The hourly frequency cap cannot exceed the monthly frequency cap.";
        }
        if (day && day > month) {
            errors["MONTH"] = "The daily frequency cap cannot exceed the monthly frequency cap.";
        }
        if (week && week > month) {
            errors["MONTH"] = "The weekly frequency cap cannot exceed the monthly frequency cap.";
        }
    }
    if (week) {
        if (week === 0) {
            errors["WEEK"] = "Frequency caps must be higher than 0";
        }
        if (hour && hour > week) {
            errors["WEEK"] = "The hourly frequency cap cannot exceed the weekly frequency cap.";
        }
        if (day && day > week) {
            errors["WEEK"] = "The daily frequency cap cannot exceed the weekly frequency cap.";
        }
    }
    if (day) {
        if (day === 0) {
            errors["DAY"] = "Frequency caps must be higher than 0";
        }
        if (hour && hour > day) {
            errors["DAY"] = "The hourly frequency cap cannot exceed the daily frequency cap.";
        }
    }
    return Object.keys(errors).length ? errors : undefined;
};
/**
 * This is a wrapper function that calls checkFrequencyProportions, which used to return a string
 * ... but now returns an object with one or more errors as key/value pairs
 * ... so this function acts as a shim for consumers that still expect a string to be returned.
 * @param caps: The frequency caps array associated with the frequencies key.
 * @returns The first error in the object, if any. Else, undefined.
 */
export const checkFrequencyProportionsOnce = (caps) => {
    const proportionErrorObj = checkFrequencyProportions(caps);
    return Object.values(proportionErrorObj ?? {})?.[0];
};
export const checkFrequencyLimits = (caps) => caps.reduce((errors, { duration: { time_unit } }) => {
    if ((Number(findFrequencyValue(caps, time_unit)) || 0) >= LimitsPerTimeframes[time_unit]) {
        if (!errors) {
            errors = {};
        }
        errors[time_unit] = `${time_unit.charAt(0) + time_unit.slice(1).toLocaleLowerCase()} frequency cap cannot exceed ${LimitsPerTimeframes[time_unit] - 1}.`;
    }
    return errors;
}, undefined);
/**
 * A wrapper function that calls checkFrequencyProportions
 * ... but only returns an error message if it's related to the specified time unit
 * @param timeUnit: The time unit you want an error message for.
 * @param caps: The frequency caps array associated with the frequencies key.
 * @returns The errorMessage if it's related to the specified time unit, otherwise undefined.
 */
export const checkFrequencyProportionFor = (timeUnit, caps) => {
    const errorMessageObj = checkFrequencyProportions(caps);
    if (!errorMessageObj)
        return undefined;
    return errorMessageObj[timeUnit];
};
/**
 * A wrapper function that calls checkFrequencyLimits
 * ... but only returns an error message if it's related to the specified time unit
 * @param timeUnit: The time unit you want an error message for.
 * @param caps: The frequency caps array associated with the frequencies key.
 * @returns The errorMessage if it's related to the specified time unit, otherwise undefined.
 */
export const checkFrequencyLimitsFor = (timeUnit, caps) => {
    const errorMessageObj = checkFrequencyLimits(caps);
    if (!errorMessageObj)
        return undefined;
    return errorMessageObj[timeUnit];
};
//# sourceMappingURL=frequency.js.map